import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Skeleton } from "@its-engineering-and-development/ui-foundation/components";
import { authClient } from "@/api/auth-client";
import { expenseAdminTokenSource } from "@/auth";
import { useAppDispatch } from "@/store";
import { setCredentials } from "@/store/auth.slice";
import { consumeAuthRedirect } from "@/utils/auth-redirect";

export function SsoCallbackPage() {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const code = searchParams.get("code");
    const hasExecutedRef = useRef(false);
    const [state, setState] = useState<"loading" | "error">("loading");
    const [error, setError] = useState<Error | null>(null);

    const execute = useCallback(
        async ({ code }: { code: string }) => {
            setState("loading");
            setError(null);

            try {
                const redirectUri = `${window.location.origin}${import.meta.env.BASE_URL}sso/callback`;
                const response = await authClient.tokenExchange({ code, redirectUri });

                authClient.setTokens(response.accessToken);
                dispatch(setCredentials({ accessToken: response.accessToken }));
                expenseAdminTokenSource.notifyTokenChange();
                navigate(consumeAuthRedirect() ?? "/", { replace: true });
            } catch (err) {
                const authError = err instanceof Error ? err : new Error(String(err));
                setError(authError);
                setState("error");
            }
        },
        [dispatch, navigate],
    );

    useEffect(() => {
        // Only execute once per code
        if (hasExecutedRef.current) return;

        if (code) {
            hasExecutedRef.current = true;
            void execute({ code }).catch(() => undefined);
        } else {
            hasExecutedRef.current = true;
            navigate("/login", { replace: true });
        }
    }, [code, execute, navigate]);

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
            <div className="w-full max-w-md space-y-8 text-center">
                {state === "loading" && (
                    <>
                        <h1 className="text-2xl font-bold text-gray-900">Verifying your identity</h1>
                        <p className="text-sm text-gray-600">This will only take a moment.</p>
                        <div className="space-y-4">
                            <Skeleton className="h-4 w-full" />
                            <Skeleton className="mx-auto h-4 w-3/4" />
                        </div>
                    </>
                )}

                {state === "error" && (
                    <>
                        <div className="flex justify-center">
                            <div className="rounded-full bg-red-100 p-4">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold text-gray-900">SSO Authentication Failed</h1>
                        <p className="text-sm text-gray-600">{error?.message || "Unable to verify your credentials."}</p>
                        <a href="/login" className="inline-block text-sm font-medium text-blue-600 hover:text-blue-800">
                            Return to Login
                        </a>
                    </>
                )}
            </div>
        </div>
    );
}

SsoCallbackPage.displayName = "SsoCallbackPage";
